import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import StyledTable from '@components/StyledTable'
import { formatDate, formatDateTime } from '@util/date-util'

import { MultilineTypography } from '../../../theme/components/MultilineTypography'
import type { Activiteit } from '../types/Activiteit'
import type { ActiviteitLog as ActiviteitLogType } from '../types/ActiviteitLog'

const logStatusLabel = (activiteit: Activiteit, index: number) => {
  const { completed, log, status } = activiteit

  if (index === 0) return 'Aangemaakt'
  if (completed && log.length > 1 && index === log.length - 1) {
    if (status === 'cancelled') return 'Geannuleerd'
    return 'Afgerond'
  }

  return 'Aangepast'
}

const logText = (
  activiteit: Activiteit,
  index: number,
  entry: ActiviteitLogType,
) => {
  const { completed, log } = activiteit

  if (completed && log.length > 1 && index === log.length - 1)
    return completed.text

  return entry.text
}

const headerCells = [
  { id: 'date', numeric: false, label: 'Datum', fixed: 80 },
  { id: 'text', numeric: false, label: 'Opmerking' },
  { id: 'dueDate', numeric: false, label: 'Opleverdatum' },
  { id: 'status', numeric: false, label: 'Mutatiesoort' },
  { id: 'email', numeric: false, label: 'Door' },
]

interface Props {
  activiteit?: Activiteit
  onClose: () => void
  open: boolean
}

export const ActiviteitLog = ({ activiteit, onClose, open }: Props) => {
  const { log = [] } = activiteit || {}

  return activiteit ? (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box role="presentation" sx={{ p: '20px' }}>
        <IconButton
          data-testid="close-button"
          onClick={onClose}
          sx={{ color: 'black' }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          sx={{
            pt: '1em',
            fontWeight: 550,
            pb: '1em',
            fontSize: '1.5rem',
            color: 'black',
          }}
          variant="h4"
        >{`Log van activiteit ${activiteit.type}`}</Typography>
        <Grid
          sx={{ p: 1, width: '95%', mx: 'auto', pt: 4, pb: 4 }}
          container
          spacing={2}
        >
          <Grid size={{ xs: 6 }}>
            <Typography gutterBottom variant="subtitle1">
              <b>Aangemaakt door</b>
            </Typography>
            <Typography variant="body2">
              {activiteit.created.organisatie}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography gutterBottom variant="subtitle1">
              <b>Toegewezen aan</b>
            </Typography>
            <Typography variant="body2">
              {activiteit.assignedTo.join(',')}
            </Typography>
          </Grid>
        </Grid>
        <StyledTable headerCells={headerCells} {...({} as any)}>
          {log.map((logEntry, index) => {
            const statusLabel = logStatusLabel(activiteit, index)
            const text = logText(activiteit, index, logEntry)

            return (
              <TableRow key={`${logEntry.date}-${logEntry.organisatie}`}>
                <TableCell>{formatDateTime(logEntry.date)}</TableCell>
                <TableCell>
                  <MultilineTypography text={text} />
                </TableCell>
                <TableCell>
                  {logEntry.dueDate ? formatDate(logEntry.dueDate) : 'Onbekend'}
                </TableCell>
                <TableCell>{statusLabel}</TableCell>
                <TableCell>
                  {logEntry.organisatie}

                  {logEntry.email ? (
                    <Typography variant="caption">
                      <br />
                      {logEntry.email}
                    </Typography>
                  ) : (
                    ''
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </StyledTable>
      </Box>
    </Drawer>
  ) : (
    <></>
  )
}
